import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAlertaCount, useAlertaTarefas, setAlertaTarefas } from "../utils/tarefasAlerta";
import { getProjetosCompleto } from "../api/projetos.api";

function getUrgency(tarefa) {
  if (tarefa.status === "Concluida" || !tarefa.data_entrega) return null;
  const hoje = new Date(new Date().toDateString());
  const prazo = new Date(tarefa.data_entrega);
  const diff  = Math.floor((prazo - hoje) / 864e5);
  if (diff < 0)  return { cor: "#f87171", label: `${Math.abs(diff)}d atraso` };
  if (diff <= 1) return { cor: "#fb923c", label: diff === 0 ? "hoje" : "amanhã" };
  return null;
}

export default function NotificacoesBell({ collapsed }) {
  const count        = useAlertaCount();
  const tarefasStore = useAlertaTarefas();
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [local,   setLocal]   = useState([]);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 228 });
  const btnRef  = useRef(null);
  const navigate = useNavigate();

  // Sincroniza do store quando ele é populado por Prazos.js
  useEffect(() => { if (tarefasStore.length > 0) setLocal(tarefasStore); }, [tarefasStore]);

  // Auto-fetch quando bell abre mas store está vazio
  const fetchUrgentes = useCallback(async () => {
    if (local.length > 0 || count === 0) return;
    setLoading(true);
    try {
      const { projetos: rows, tarefas: all } = await getProjetosCompleto(true, 2);
      const tMap = {};
      (rows || []).forEach(r => {
        if (r.ticketid && !tMap[r.ticketid])
          tMap[r.ticketid] = { id: r.ticketid, subject: r.subject, descricao_customizada: r.descricao_customizada, projeto: r.descricao };
      });
      const urgent = (all || [])
        .filter(t => getUrgency(t) !== null)
        .slice(0, 20)
        .map(t => ({ ...t, _ticket: tMap[t.id_ticket] || null }));
      setLocal(urgent);
      setAlertaTarefas(urgent);
    } catch {}
    setLoading(false);
  }, [local.length, count]);

  useEffect(() => { if (open) fetchUrgentes(); }, [open, fetchUrgentes]);

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return;
    const fn = e => { if (btnRef.current && !btnRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [open]);

  // Fecha com Esc
  useEffect(() => {
    if (!open) return;
    const fn = e => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [open]);

  function handleClick() {
    if (!open && btnRef.current) {
      const rect        = btnRef.current.getBoundingClientRect();
      const panelH      = Math.min(480, window.innerHeight - 80);
      const idealTop    = rect.top - panelH / 2;
      const clampedTop  = Math.max(60, Math.min(idealTop, window.innerHeight - panelH - 20));
      setPanelPos({ top: clampedTop, left: rect.right + 8 });
    }
    setOpen(v => !v);
  }

  const items = local.slice(0, 8);

  return (
    <div ref={btnRef} style={{ position: "relative" }}>
      <button
        style={{
          ...bellBtn,
          justifyContent: collapsed ? "center" : "flex-start",
          background: open ? "rgba(96,165,250,0.07)" : "transparent",
        }}
        onClick={handleClick}
        title={collapsed ? `${count} urgente${count !== 1 ? "s" : ""}` : undefined}
      >
        <span style={{ fontSize: 16, lineHeight: 1 }}>🔔</span>
        {!collapsed && (
          <span style={{ fontSize: 12, color: count > 0 ? "#f87171" : "#475569" }}>
            {count > 0 ? `${count} urgente${count !== 1 ? "s" : ""}` : "Sem alertas"}
          </span>
        )}
        {count > 0 && <span style={badge}>{count > 99 ? "99+" : count}</span>}
      </button>

      {open && (
        <div style={{ ...panel, top: panelPos.top, left: panelPos.left, bottom: 20 }}>
          <div style={panelHead}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9" }}>🔔 Tarefas urgentes</span>
            <button style={closeBtn} onClick={() => setOpen(false)}>✕</button>
          </div>

          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading && <div style={emptyMsg}>Carregando...</div>}
            {!loading && items.length === 0 && (
              <div style={emptyMsg}>Nenhuma tarefa urgente 🎉</div>
            )}
            {!loading && items.map(t => {
              const urg = getUrgency(t);
              if (!urg) return null;
              return (
                <div
                  key={t.id_tarefa}
                  style={item}
                  onClick={() => { navigate("/tarefas/prazos"); setOpen(false); }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(96,165,250,0.05)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <span style={{ ...urgBadge, color: urg.cor, borderColor: urg.cor + "44", background: urg.cor + "15" }}>
                    {urg.label}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={itemDesc}>{t.descritivo}</div>
                    {t._ticket && (
                      <div style={itemTicket}>{t._ticket.descricao_customizada || t._ticket.subject}</div>
                    )}
                    {t.responsavel && <div style={itemResp}>👤 {t.responsavel}</div>}
                  </div>
                </div>
              );
            })}
          </div>

          <div
            style={footer}
            onClick={() => { navigate("/tarefas/prazos"); setOpen(false); }}
            onMouseEnter={e => e.currentTarget.style.color = "#93c5fd"}
            onMouseLeave={e => e.currentTarget.style.color = "#60a5fa"}
          >
            Ver todos os prazos →
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Estilos ─────────────────────────────────────────────────────── */

const bellBtn = {
  display: "flex", alignItems: "center", gap: 10,
  padding: "8px 10px", borderRadius: 10, border: "none",
  cursor: "pointer", width: "100%", position: "relative",
  transition: "background 0.15s",
};

const badge = {
  position: "absolute", top: 4, left: 20,
  background: "#f87171", color: "#fff",
  borderRadius: 8, padding: "0 4px",
  fontSize: 9, fontWeight: 800,
  minWidth: 14, height: 14,
  display: "flex", alignItems: "center", justifyContent: "center",
  lineHeight: 1, border: "1.5px solid #060d1e",
};

const panel = {
  position: "fixed", width: 320, zIndex: 9999,
  background: "rgba(8,18,42,0.98)",
  border: "1px solid rgba(96,165,250,0.18)",
  borderRadius: 14, boxShadow: "0 8px 40px rgba(0,0,0,0.55)",
  backdropFilter: "blur(20px)",
  display: "flex", flexDirection: "column",
};

const panelHead = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "14px 16px 10px", borderBottom: "1px solid rgba(96,165,250,0.1)",
};

const closeBtn = {
  background: "transparent", border: "none", color: "#475569",
  cursor: "pointer", fontSize: 13, padding: "2px 6px", borderRadius: 5,
};

const emptyMsg = {
  padding: "20px 16px", color: "#475569", fontSize: 13, textAlign: "center",
};

const item = {
  display: "flex", alignItems: "flex-start", gap: 10,
  padding: "10px 16px", borderBottom: "1px solid rgba(96,165,250,0.06)",
  cursor: "pointer", transition: "background 0.1s",
};

const urgBadge = {
  fontSize: 10, fontWeight: 700, border: "1px solid",
  borderRadius: 5, padding: "2px 6px", whiteSpace: "nowrap", marginTop: 2, flexShrink: 0,
};

const itemDesc = {
  fontSize: 12, fontWeight: 500, color: "#e2e8f0",
  lineHeight: 1.35, overflow: "hidden",
  textOverflow: "ellipsis", whiteSpace: "nowrap",
};

const itemTicket = {
  fontSize: 10, color: "#60a5fa", marginTop: 2,
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};

const itemResp = { fontSize: 10, color: "#64748b", marginTop: 1 };

const footer = {
  padding: "12px 16px", color: "#60a5fa", fontSize: 12,
  fontWeight: 600, cursor: "pointer", textAlign: "center",
  borderTop: "1px solid rgba(96,165,250,0.1)", transition: "color 0.15s",
};
